/**
 * PORTED — RO-3-11 / #44304.
 * Origin: `excellent/app/database/views/_client.tsx`.
 *
 * Changes, and only these:
 *   `"use client"`                           -> dropped (inert in this host)
 *   `useSearchParams` from next/navigation   -> `useSearch` from @excellent/ui/router
 *   the local `KindOption` interface          -> the contract's `ViewKindOptionDto`
 *
 * `useSearch()` from the seam and not this route's typed `useSearch`: `?q=` is a
 * SHELL-WIDE key (`client/shell/search.ts` reserves it — the dock owns the query,
 * the view mirrors it, CLAUDE.md rule 7), so reading it untyped through the seam is
 * correct here and does not steal a name. The `ScopedSearchButton` in the header is
 * unchanged, which is what keeps this page on the blessed scoped-search pattern
 * rather than an inline bar (Issue 19).
 *
 * The kind catalog moved from a server component's props to the
 * `objectTypes.kinds` endpoint, which is why the type swap matters: the per-kind
 * `coreFields` allowlist is what stops the filter builder offering a field the
 * saved-view compiler would refuse, and it must keep coming from the same place
 * that compiler's own allowlist does. It does — see the endpoint.
 *
 * `savedViews.list` / `.create` / `.run` are all mounted, so this page is live.
 */

import { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  DataTable,
  DodPredicatesTable,
  FormField,
  IntentBadge,
  Input,
  PageHeader,
  SavedView,
  ScopedSearchButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  formatRelativeTime,
  viewPredicateSummary,
  type DataTableColumn,
  type Intent,
} from "@excellent/ui";
import { Plus, X } from "@excellent/ui/icons";
import { useApi, apiCall, apiKey } from "@excellent/ui/api";
import { useSWRConfig } from "swr";
import {
  savedViews,
  type SavedViewRowDto,
  type ViewFilterDto,
  type SavedViewRunResponse,
} from "@excellent/ui/api/contracts/database/saved-views";
import type { ViewKindOptionDto } from "@excellent/ui/api/contracts/database/object-types";
import { dodPredicates } from "@excellent/ui/api/contracts/database/dod-predicates";
import { useSearch } from "@excellent/ui/router";

const OPS: Array<{ value: ViewFilterDto["op"]; label: string; needsValue: boolean; array?: boolean }> = [
  { value: "eq", label: "equals", needsValue: true },
  { value: "neq", label: "not equals", needsValue: true },
  { value: "contains", label: "contains", needsValue: true },
  { value: "gt", label: "greater than", needsValue: true },
  { value: "lt", label: "less than", needsValue: true },
  { value: "gte", label: "≥", needsValue: true },
  { value: "lte", label: "≤", needsValue: true },
  { value: "in", label: "is one of", needsValue: true, array: true },
  { value: "is-null", label: "is empty", needsValue: false },
  { value: "not-null", label: "is not empty", needsValue: false },
];

const ORIGIN_INTENT: Record<SavedViewRowDto["origin"], Intent> = {
  system: "neutral",
  user: "info",
  agent: "success",
};

interface DraftFilter {
  field: string;
  op: ViewFilterDto["op"];
  value: string;
}

/**
 * The Views workbench (Data Design PRD §6.1). Lists saved views, builds a new
 * one (kind picker → filter rows → column picker), and opens a view — rendered
 * by the shared `SavedView` block. The page owns the search query; the create
 * form only ever offers fields from the selected kind's allowlist, so an
 * invalid view can't be authored.
 */
export function ViewsClient({ kinds }: { kinds: ViewKindOptionDto[] }) {
  const { mutate } = useSWRConfig();
  // The dock owns the query, scoped to this page (Issue 19 / SCOPED-SEARCH-PRD);
  // the page mirrors it from the URL `?q=` and filters its own rows.
  const searchParams = useSearch();
  const query = searchParams.get("q") ?? "";
  const [creating, setCreating] = useState(false);
  const [openUid, setOpenUid] = useState<string | null>(null);

  const { data } = useApi(savedViews.list, { query: {} }, { refreshInterval: 0 });
  const { data: predicateData } = useApi(dodPredicates.list, { query: {} });
  const views = data?.views ?? [];
  const predicates = predicateData?.predicates ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return views;
    return views.filter((v) => v.name.toLowerCase().includes(q) || v.entity_kind.toLowerCase().includes(q));
  }, [views, query]);

  const columns = useMemo<DataTableColumn<SavedViewRowDto>[]>(
    () => [
      { key: "name", header: "Name", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
      { key: "entity_kind", header: "Kind", render: (r) => <span className="font-mono type-subtext text-muted-foreground">{r.entity_kind}</span> },
      {
        key: "visibility",
        header: "Visibility",
        render: (r) => <span className="text-muted-foreground">{r.visibility === "private" ? "Private" : "Workspace"}</span>,
      },
      {
        key: "origin",
        header: "Origin",
        render: (r) => (
          <IntentBadge intent={ORIGIN_INTENT[r.origin]}>
            {r.origin === "system" ? "System" : r.origin === "user" ? "User" : "Agent"}
          </IntentBadge>
        ),
      },
      {
        key: "predicate",
        header: "Predicate",
        render: (r) => {
          const summary = viewPredicateSummary(r.uid, predicates);
          if (!summary) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="flex flex-wrap items-center gap-1">
              <IntentBadge intent={summary.stance === "confirmed" ? "success" : "warning"}>
                {summary.stance === "proposal" ? "Proposal" : "Confirmed"}
              </IntentBadge>
              <IntentBadge intent={summary.independence === "independent" ? "info" : "neutral"}>
                {summary.independence === "independent" ? "INDEPENDENT" : "DEPENDENT"}
              </IntentBadge>
            </span>
          );
        },
      },
      { key: "updated", header: "Updated", render: (r) => <span className="text-muted-foreground">{formatRelativeTime(r.updated_at)}</span> },
    ],
    [predicates],
  );

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title="Views"
          description="Saved queries over any object kind — filter core columns and custom fields, no SQL required."
          toolbar={
            <div className="flex items-center gap-2">
              <ScopedSearchButton label="Search views" />
              <Button
                size="icon-tb"
                aria-label={creating ? "Cancel" : "New view"}
                title={creating ? "Cancel" : "New view"}
                onClick={() => setCreating((c) => !c)}
              >
                {creating ? <X strokeWidth={1.5} className="size-4" /> : <Plus strokeWidth={1.5} className="size-4" />}
              </Button>
            </div>
          }
        />

        {creating && (
          <CreateViewForm
            kinds={kinds}
            onSaved={async (uid) => {
              setCreating(false);
              await mutate(apiKey(savedViews.list, { query: {} }));
              setOpenUid(uid);
            }}
          />
        )}

        <DodPredicatesTable />

        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.uid}
          strongHeader
          onRowClick={(r) => setOpenUid((cur) => (cur === r.uid ? null : r.uid))}
          empty={query.trim() ? `No views match “${query.trim()}”.` : "No saved views yet. Create one to get started."}
        />

        {openUid && <OpenView uid={openUid} name={views.find((v) => v.uid === openUid)?.name ?? "View"} />}
      </div>
    </div>
  );
}

/** Renders one saved view's rows through the shared block. */
function OpenView({ uid, name }: { uid: string; name: string }) {
  const { data, error } = useApi(savedViews.run, { query: { uid } });
  if (error) {
    return (
      <Card>
        <CardContent className="py-6 type-body text-muted-foreground">Could not run this view.</CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <CardContent className="py-6 type-body text-muted-foreground">Running…</CardContent>
      </Card>
    );
  }
  return <SavedView title={name} columns={data.columns} rows={data.rows} total={data.total} />;
}

function CreateViewForm({ kinds, onSaved }: { kinds: ViewKindOptionDto[]; onSaved: (uid: string) => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState(kinds[0]?.kind ?? "");
  const [filters, setFilters] = useState<DraftFilter[]>([]);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const active = kinds.find((k) => k.kind === kind);
  const fieldOptions = useMemo(() => {
    if (!active) return [] as Array<{ value: string; label: string }>;
    return [
      ...active.coreFields.map((f) => ({ value: f, label: f })),
      ...active.customFields.map((f) => ({ value: `cf:${f.slug}`, label: `${f.label} (custom)` })),
    ];
  }, [active]);

  const addFilter = () => setFilters((f) => [...f, { field: fieldOptions[0]?.value ?? "", op: "eq", value: "" }]);
  const removeFilter = (i: number) => setFilters((f) => f.filter((_, j) => j !== i));
  const setFilter = (i: number, patch: Partial<DraftFilter>) =>
    setFilters((f) => f.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const toggleCol = (key: string) =>
    setSelectedCols((c) => (c.includes(key) ? c.filter((k) => k !== key) : [...c, key]));

  const save = async () => {
    setErr(null);
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        entity_kind: kind,
        query: {
          match: "all" as const,
          filters: filters
            .filter((f) => f.field)
            .map((f) => {
              const opDef = OPS.find((o) => o.value === f.op)!;
              if (!opDef.needsValue) return { field: f.field, op: f.op };
              const value = opDef.array ? f.value.split(",").map((s) => s.trim()).filter(Boolean) : f.value;
              return { field: f.field, op: f.op, value };
            }),
        },
        columns: selectedCols.length
          ? selectedCols.map((key) => ({ key, label: key.startsWith("cf:") ? key.slice(3) : key, type: "text" as const }))
          : null,
      };
      const res = await apiCall(savedViews.create, { body });
      onSaved(res.view.uid);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not create the view.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 py-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. In-transit shipments" />
          </FormField>
          <FormField label="Object kind" required>
            <Select value={kind} onValueChange={(v) => { setKind(v); setFilters([]); setSelectedCols([]); }}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a kind" />
              </SelectTrigger>
              <SelectContent>
                {kinds.map((k) => (
                  <SelectItem key={k.kind} value={k.kind}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="type-subtext font-medium text-muted-foreground">Filters</span>
            <Button variant="ghost" size="sm" onClick={addFilter} disabled={!fieldOptions.length}>
              <Plus strokeWidth={1.5} className="size-4" />
              Add filter
            </Button>
          </div>
          {filters.map((f, i) => {
            const opDef = OPS.find((o) => o.value === f.op)!;
            return (
              <div key={i} className="flex items-center gap-2">
                <Select value={f.field} onValueChange={(v) => setFilter(i, { field: v })}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Field" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={f.op} onValueChange={(v) => setFilter(i, { op: v as ViewFilterDto["op"] })}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {opDef.needsValue && (
                  <Input
                    value={f.value}
                    onChange={(e) => setFilter(i, { value: e.target.value })}
                    placeholder={opDef.array ? "a, b, c" : "value"}
                    className="flex-1"
                  />
                )}
                <Button variant="ghost" size="sm" onClick={() => removeFilter(i)}>
                  <X strokeWidth={1.5} className="size-4" />
                </Button>
              </div>
            );
          })}
        </div>

        {active && (
          <div className="flex flex-col gap-2">
            <span className="type-subtext font-medium text-muted-foreground">Columns (optional — defaults if none picked)</span>
            <div className="flex flex-wrap gap-2">
              {[...active.coreFields, ...active.customFields.map((c) => `cf:${c.slug}`)].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleCol(key)}
                  className={
                    selectedCols.includes(key)
                      ? "rounded-full border border-primary bg-primary/10 px-3 py-1 type-subtext text-foreground"
                      : "rounded-full border border-border/60 px-3 py-1 type-subtext text-muted-foreground"
                  }
                >
                  {key.startsWith("cf:") ? key.slice(3) : key}
                </button>
              ))}
            </div>
          </div>
        )}

        {err && <p className="type-subtext text-destructive">{err}</p>}

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !name.trim() || !kind}>
            {busy ? "Saving…" : "Save view"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
